import type { SxProps, Theme } from '@mui/material/styles';

import { useState, useEffect } from 'react';

import { Box } from '@mui/material';

import { GLYPH_NEUTRAL, DOCUMENT_TYPE_META } from './documents-type-meta';

import type { DocumentType } from './documents-data';

type Props = {
  src: string | null;
  alt: string;
  type: DocumentType;
  /** Glyph size for the fallback. Cards are small; the dialog is not. */
  glyphSize?: number;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

/** Shows the file type's glyph when there is no preview or it fails to load. */
export function DocumentPreviewImage({ src, alt, type, glyphSize = 32, sx, ...rest }: Props) {
  const [failed, setFailed] = useState(false);

  // A new src deserves its own attempt — otherwise one failure would keep the
  // fallback up for every later document rendered through the same element.
  useEffect(() => setFailed(false), [src]);

  if (failed || !src) {
    const meta = DOCUMENT_TYPE_META[type];
    return (
      <Box
        role="img"
        aria-label={alt}
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'container.medium',
            // One neutral grey for every type here, not the per-type badge
            // colours: a wall of missing thumbnails should read as one quiet
            // placeholder state, not as six different coloured tiles.
            color: GLYPH_NEUTRAL,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {meta.renderIcon(glyphSize)}
      </Box>
    );
  }

  // A document's preview is its full page image, so only the cards on screen fetch one.
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      sx={sx}
      {...rest}
    />
  );
}
